import React, { useEffect, useState } from 'react'
import { Button, Card, DatePicker, Select, Spin, Table } from 'antd'
import moment from 'moment'
import * as XLSX from 'xlsx'
import BaseListing from 'src/BaseListings'
import { wtApiCall } from 'src/helpers/commons/ApiWrapper'
import { lzNotifyErrorMessageFromResult } from 'src/helpers/commons/notifications'
import WidgetItemSelector from 'src/widgets/Selectors/WidgetItemSelector'

const formatQuantity = (value) =>
  Number(value || 0) === 0
    ? '-'
    : Number(value).toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })

export default function OutletWiseStockReport() {
  const base = new BaseListing()
  const [date, setDate] = useState(moment().format('YYYY-MM-DD'))
  const [itemId, setItemId] = useState(0)
  const [itemName, setItemName] = useState('')
  const [items, setItems] = useState([])
  const [outletId, setOutletId] = useState(0)
  const [outlets, setOutlets] = useState([])
  const [rows, setRows] = useState([])
  const [columns, setColumns] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const tableWidth = 706 + outlets.length * 86

  const buildExportRows = (reportRows = rows) =>
    reportRows.map((row, index) => {
      const exportRow = {
        '#': index + 1,
        Code: row.item?.code || '',
        Category: row.item?.item_category?.name || '',
        'Item Name': row.item?.name || '',
        Brand: row.item_brand?.name || '',
        UoM: row.item_unit?.name || '',
      }
      outlets.forEach((outlet) => {
        exportRow[outlet.name] = row[`outlet_${outlet.id}`] || 0
      })
      exportRow.Total = row.total_quantity || 0
      return exportRow
    })

  const exportExcel = () => {
    const exportRows = buildExportRows()
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(exportRows), 'Outlet Stock')
    XLSX.writeFile(workbook, `outlet_wise_stock_report_${date}.xlsx`)
  }

  const exportExcelWithSerials = () => {
    setIsLoading(true)
    wtApiCall(
      'reports/outlet_wise_stock_report',
      { date, item_id: itemId, outlet_id: outletId, include_serials: 1 },
      'get',
      (response) => {
        const report = response?.data || {}
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(
          workbook,
          XLSX.utils.json_to_sheet(buildExportRows()),
          'Outlet Stock',
        )
        XLSX.utils.book_append_sheet(
          workbook,
          XLSX.utils.json_to_sheet(report.serial_data || []),
          'Item Serials',
        )
        XLSX.writeFile(workbook, `outlet_wise_stock_report_with_serial_${date}.xlsx`)
        setIsLoading(false)
      },
      (error) => {
        lzNotifyErrorMessageFromResult(error)
        setIsLoading(false)
      },
    )
  }

  const getReportData = (filters = {}) => {
    setIsLoading(true)
    wtApiCall(
      'reports/outlet_wise_stock_report',
      {
        date: filters.date || date,
        item_id: filters.item_id ?? itemId,
        outlet_id: filters.outlet_id ?? outletId,
      },
      'get',
      (response) => {
        const report = response?.data || {}
        const reportOutlets = [...(report.outlets || [])].sort(
          (left, right) =>
            (Number(left.sort_order) > 0 ? Number(left.sort_order) : Number.MAX_SAFE_INTEGER) -
              (Number(right.sort_order) > 0 ? Number(right.sort_order) : Number.MAX_SAFE_INTEGER) ||
            String(left.name || '').localeCompare(String(right.name || '')) ||
            Number(left.id) - Number(right.id),
        )
        const reportRows = report.data || []
        const outletColumns = reportOutlets.map((outlet) => ({
          title: outlet.name,
          key: `outlet_${outlet.id}`,
          dataIndex: `outlet_${outlet.id}`,
          width: 86,
          align: 'right',
          render: (value) => formatQuantity(value),
        }))
        setOutlets(reportOutlets)
        setRows(reportRows)
        setColumns([
          {
            title: '#',
            key: 'index',
            width: 42,
            render: (_, __, index) => index + 1,
          },
          {
            title: 'Code',
            key: 'code',
            width: 92,
            render: (_, record) => record.item?.code || '',
          },
          {
            title: 'Category',
            key: 'category',
            width: 120,
            ellipsis: true,
            render: (_, record) => record.item?.item_category?.name || '',
          },
          {
            title: 'Item Name',
            key: 'product_name',
            width: 190,
            ellipsis: true,
            render: (_, record) => record.item?.name || '',
          },
          {
            title: 'Brand',
            key: 'brand',
            width: 110,
            ellipsis: true,
            render: (_, record) => record.item_brand?.name || '',
          },
          {
            title: 'UoM',
            key: 'unit_of_measure',
            width: 62,
            ellipsis: true,
            render: (_, record) => record.item_unit?.name || '',
          },
          ...outletColumns,
          {
            title: 'Total',
            key: 'total_quantity',
            dataIndex: 'total_quantity',
            width: 90,
            align: 'right',
            render: (value) => formatQuantity(value),
          },
        ])
        setIsLoading(false)
      },
      (error) => {
        lzNotifyErrorMessageFromResult(error)
        setIsLoading(false)
      },
    )
  }

  useEffect(() => {
    wtApiCall(
      'dropdown',
      { dropdown_names: 'items,outlets' },
      'get',
      (response) => {
        setItems(response?.data?.[0]?.items || [])
        getReportData()
      },
      (error) => {
        lzNotifyErrorMessageFromResult(error)
        getReportData()
      },
    )
  }, [])

  return (
    <div
      className="container-fluid"
      style={{ padding: '1em', backgroundColor: 'white', minHeight: '100vh' }}
    >
      <Card
        title="Outlet Wise Stock Report"
        extra={
          base.hasRights('Export Outlet Wise Stock Report') && (
            <div className="d-flex gap-2">
              <Button onClick={exportExcel} disabled={isLoading || rows.length === 0}>
                Export Excel
              </Button>
              <Button onClick={exportExcelWithSerials} disabled={isLoading || rows.length === 0}>
                Export Excel With Serial
              </Button>
            </div>
          )
        }
      >
        <div className="d-flex flex-wrap gap-3 align-items-end mb-3">
          <div>
            <label>As Of</label>
            <DatePicker
              value={moment(date)}
              format="YYYY-MM-DD"
              onChange={(value) => value && setDate(value.format('YYYY-MM-DD'))}
            />
          </div>
          <div style={{ minWidth: 220 }}>
            <label>Outlet</label>
            <Select
              allowClear
              value={outletId || undefined}
              placeholder="All Outlets"
              options={outlets.map((outlet) => ({ value: outlet.id, label: outlet.name }))}
              onChange={(value) => setOutletId(value || 0)}
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ minWidth: 360 }}>
            <label>Item Name</label>
            <WidgetItemSelector
              items={items}
              value={itemId}
              displayValue={itemName}
              placeHolder="Select Item"
              reloadDropdownData={() => setItems(items)}
              onChange={(value) => {
                setItemId(value?.value || 0)
                setItemName(value?.label || '')
              }}
            />
          </div>
          <button
            className="btn btn-primary"
            type="button"
            onClick={() => getReportData()}
            disabled={isLoading}
          >
            Generate
          </button>
        </div>
        <Table
          columns={columns}
          dataSource={rows}
          pagination={false}
          bordered
          size="small"
          rowKey="key"
          loading={{ indicator: <Spin />, spinning: isLoading }}
          tableLayout="fixed"
          scroll={{ x: tableWidth }}
        />
      </Card>
    </div>
  )
}
